'use client';

import React, { type Dispatch, type SetStateAction } from 'react';

import { useTranslations } from 'next-intl';

import { EbayButton } from '../EbayButton';

import { useEbayAuth } from '@/modules/auth/useEbayAuth';
import { useRedirectQueryParam } from '@/modules/auth/useRedirectQueryParam';
import { fetchEbayUserCredentials } from '@/modules/login/api';
import {
  handleSSOError,
  setAccountLinkingCookies,
} from '@/modules/login/helpers';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { Stdv1Stat } from '@/modules/observability/constants';
import { recordMetric } from '@/modules/observability/metrics';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { useSSOSignup } from '@/modules/signup/hooks/useSSOSignup';
import { useSignupTracking } from '@/modules/signup/hooks/useSignupTracking';
import { SignupType } from '@/modules/signup/types';

type Props = {
  setError: Dispatch<SetStateAction<string>>;
};

export function EbaySignupButton({ setError }: Props) {
  const t = useTranslations('signup');
  const { sendSignupSubmitActionEvent } = useSignupTracking();
  const { ebayLoginMutation } = useLoginMutations({
    loginType: LoginType.Ebay,
  });
  const { signupWithSSO } = useSSOSignup({ ssoType: SignupType.Ebay });
  const router = useExtendedRouter();
  const redirectQueryParam = useRedirectQueryParam();

  const errorMessage = t('Error.SocialProvider', {
    socialProvider: 'eBay',
  });

  function handleAccountNotLinked(
    ebay_user_id: string,
    ebay_token_reference: string,
    email: string
  ) {
    setAccountLinkingCookies({ ebay_user_id, ebay_token_reference }, email);
    router.push(`/login/${redirectQueryParam}`);
  }

  async function handleEbayAuthorized(code: string) {
    let ebay_user_id: string;
    let ebay_token_reference: string;
    let email: string;
    let first_name: string | null | undefined;

    try {
      ({
        data: { ebay_user_id, ebay_token_reference, email, first_name },
      } = await fetchEbayUserCredentials(code));
    } catch {
      recordMetric({
        type: 'increment',
        stat: Stdv1Stat['ebay.auth.credentials_error'],
      });
      return setError(errorMessage);
    }

    ebayLoginMutation.mutate(
      { ebay_user_id, ebay_token_reference },
      {
        onError: (error) => {
          handleSSOError({
            error,
            setError: () => setError(errorMessage),
            onUserNotFound: () => {
              signupWithSSO({
                name: { firstName: first_name ?? '', lastName: '' },
                email,
                credentials: {
                  ebay_user_id,
                  ebay_token_reference,
                },
              });
            },
            onAccountNotLinked: () =>
              handleAccountNotLinked(ebay_user_id, ebay_token_reference, email),
          });
        },
      }
    );
  }

  const { startPopupFlow, isInProgress } = useEbayAuth({
    onEbayAuthorized: handleEbayAuthorized,
    onError: () => setError(errorMessage),
  });

  function handleClick() {
    sendSignupSubmitActionEvent(SignupType.Ebay);
    startPopupFlow();
  }

  return (
    <EbayButton
      onClick={handleClick}
      disabled={isInProgress || ebayLoginMutation.isPending}
    />
  );
}
